const singleSelectTypes = require('./digital-pay-single-select-type.constants');
const multiSelectTypes = require('./digital-pay-multi-select-type.constants');
const {
    concat, curry, filter, find, groupBy, keys, map, max, size, some,
} = require('lodash');

/**
 * Handles Payment Specific Display Logic.
 *
 * Purpose of this component:
 * 1. Display Payment Instruments
 * 2. Maintain Payment Instrument display states.
 *
 * Note 1: This component should not concern itself about making payments.
 *
 */
class DigitalPayInstrumentsController {
    constructor($q, paymentDistributionService, shopperService, settings, digitalPayDisablePaymentStateService, dateHelperService, digitalPayPaymentErrorDisplayService, digitalPayErrorMessageGenerator, digitalPayPaymentInstrumentsService) {
        this.$q = $q;
        this.paymentDistributionService = paymentDistributionService;
        this.shopperService = shopperService;
        this.settings = settings;
        this.disablePaymentStateService = digitalPayDisablePaymentStateService;
        this.dateHelperService = dateHelperService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
        this.digitalPayPaymentInstrumentsService = digitalPayPaymentInstrumentsService;
    }

    $onInit() {
        // Key used for identifying amount distributed to add gift card component.
        this.gcPaymentAddCardAmountKey = 'gcAddCardAmount';
        // Key used for identifying amount distributed to on account component.
        this.onAccountPaymentAmountKey = 'onAccountPaymentAmountKey';

        // Default pre-selected payment instrument value definitions
        this.singleSelectType = singleSelectTypes.NONE;
        this.shouldSelectGiftCard = false;

        // On Account
        this.isEligibleForOnAccount = this.shopperService.shopper.IsRegisteredForDirectDebit;

        // State of instruments
        this.isDigitalPayReady = false;
        this.hasListInstrumentsError = false;

        // Initializing error page
        this.serviceUnavailablePrimaryMessage = this.settings.digitalPay.paymentConnectivityPrimaryErrorMessage;
        this.serviceUnavailableSecondaryMessage = this.isEligibleForOnAccount
            ? this.settings.digitalPayMessages.accountPaymentWhenListUnavailableMessage
            : this.settings.digitalPay.paymentConnectivitySecondaryErrorMessage;

        // Keeps track of all the non single select type
        this.multipleSelectCards = {};

        this.loadInstruments()
            .then(() => {
                this.isImpersonated = this.instruments.IsImpersonated;
                this.showOnlyOnAccount = this.instruments.Account.Enabled
                    && !(this.instruments.CreditCard.Enabled || this.instruments.GiftCard.Enabled || this.instruments.Paypal.Enabled);
                this.preSelectPaymentInstruments();
            })
            .catch(() => {
                if (this.isEligibleForOnAccount) {
                    this.singleSelectType = singleSelectTypes.ACCOUNT;
                }
            })
            .finally(() => {
                this.isDigitalPayReady = true;
            });

        this.notifyInstrumentTypeInManageMode(this.settings.digitalPayPaymentTypes.emptyType);
    }

    /**
     * Load payment instruments
     */
    loadInstruments() {
        return this.digitalPayPaymentInstrumentsService.listDigitalPayInstruments(this.isForPaymentAgreement).then((response) => {
            if (!response.Success) {
                this.hasListInstrumentsError = true;
                return this.$q.reject();
            }

            this.instruments = response;
            this.hasListInstrumentsError = false;
            return response;
        });
    }

    preSelectPaymentInstruments() {
        // Pre-selection of instruments
        const paymentInstruments = this.normalizePaymentInstruments(this.instruments.CreditCard.Instruments, this.instruments.Paypal.Instruments, this.instruments.GiftCard.Instruments);
        const lastUsedPaymentInstruments = this.getLastUsedPaymentInstruments(paymentInstruments);
        const shouldSelectOnAccount = this.shouldSelectOnAccount(lastUsedPaymentInstruments, this.showOnlyOnAccount, this.instruments.Account.Attributes.AccountIsLastUsed, this.isEligibleForOnAccount);
        if (shouldSelectOnAccount) {
            this.singleSelectType = singleSelectTypes.ACCOUNT;
        } else {
            this.setSelectInstruments(lastUsedPaymentInstruments);
        }
    }

    /**
     * Normalizes a single payment instrument
     * @param {string} type
     * @param {Object} instrument
     */
    normalizePaymentInstrument(type, instrument) {
        return {
            Id: instrument.PaymentInstrumentId,
            LastUsed: instrument.LastUsed,
            Type: type,
        };
    }

    /**
     * Returns a list of normalized payment instruments
     * We can add new payment instrument types here
     * @param {Object[]} creditCards
     * @param {Object[]} paypal
     * @param {Object[]} giftCards
     */
    normalizePaymentInstruments(creditCards, paypal, giftCards) {
        const creditCardInstruments = map(creditCards, curry(this.normalizePaymentInstrument)(singleSelectTypes.CREDITCARD));
        const paypalInstruments = map(paypal, curry(this.normalizePaymentInstrument)(singleSelectTypes.PAYPAL));
        const giftCardInstruments = map(giftCards, curry(this.normalizePaymentInstrument)(multiSelectTypes.GIFTCARD));
        return concat(creditCardInstruments, paypalInstruments, giftCardInstruments);
    }

    /**
     * Groups a list of normalized payment instruments by their last used
     * Returns the group which has the highest last used
     * @param {Object[]} instruments
     */
    getLastUsedPaymentInstruments(instruments) {
        const instrumentGroups = groupBy(instruments, instrument => this.dateHelperService.getDateFromEpochInSeconds(instrument.LastUsed));
        const instrumentGroupKeys = keys(instrumentGroups);
        const maxInstrumentGroupKey = max(instrumentGroupKeys);
        return instrumentGroups[maxInstrumentGroupKey];
    }

    /**
     * Determines if we should select on account or not
     * @param {Object[]} lastUsedPaymentInstruments
     * @param {boolean} showOnlyOnAccount
     * @param {boolean} accountIsLastUsed
     * @param {boolean} isEligibleForOnAccount
     */
    shouldSelectOnAccount(lastUsedPaymentInstruments, showOnlyOnAccount, accountIsLastUsed, isEligibleForOnAccount) {
        return showOnlyOnAccount ||
            ((!lastUsedPaymentInstruments || !lastUsedPaymentInstruments.length) && isEligibleForOnAccount) ||
            (accountIsLastUsed && isEligibleForOnAccount);
    }

    /**
     * Single select type takes precedence over multi select types
     * When more multi select types are added, this method will need to change
     * @param {Object[]} lastUsedPaymentInstruments
     */
    setSelectInstruments(lastUsedPaymentInstruments) {
        if (lastUsedPaymentInstruments && lastUsedPaymentInstruments.length) {
            const singleSelectType = find(lastUsedPaymentInstruments, instrument => some(singleSelectTypes, type => type === instrument.Type));
            if (singleSelectType) {
                this.singleSelectType = singleSelectType.Type;
            } else {
                this.shouldSelectGiftCard = true;
            }
        }
    }

    notifyPaypalSelected() {
        this.updateShowPaypalPlaceOrderButton();
    }

    notifyPaypalUnselected() {
        this.shouldShowPaypalPlaceOrderButton = false;
    }

    notifyInstrumentTypeInManageMode(type) {
        this.instrumentTypeInManageMode = type;
        this.onChildIsInManageMode({ state: this.instrumentTypeInManageMode !== this.settings.digitalPayPaymentTypes.emptyType });
    }

    notifyChangeMultiSelectCard(id, isSelected) {
        if (isSelected) {
            this.multipleSelectCards[id] = {};
        } else {
            delete this.multipleSelectCards[id];
        }

        this.updateShowPaypalPlaceOrderButton();
    }

    notifyPayPalLinkFailed(errorMessage) {
        this.digitalPayPaymentErrorDisplayService.pushGlobalError(errorMessage);
    }

    notifyPayPalUnlinkFailed(errorMessage) {
        this.digitalPayPaymentErrorDisplayService.pushGlobalError(errorMessage);
    }

    shouldDisplayPaymentInstrumentList(type) {
        return this.instrumentTypeInManageMode === this.settings.digitalPayPaymentTypes.emptyType ||
            this.instrumentTypeInManageMode === type;
    }

    setSingleSelectType(type) {
        this.singleSelectType = type;
        this.onCreditCardSelectionChanged({ state: type === singleSelectTypes.CREDITCARD });
    }

    updateShowPaypalPlaceOrderButton() {
        const paymentKeys = this.paymentDistributionService.getKeys();
        this.shouldShowPaypalPlaceOrderButton = paymentKeys.length === 1 &&
            this.singleSelectType === singleSelectTypes.PAYPAL &&
            !size(this.multipleSelectCards);
    }

    reloadInstruments() {
        this.hasListInstrumentsError = false;
        this.isDigitalPayReady = false;
        this.loadInstruments().finally(() => {
            this.isDigitalPayReady = true;
        });
    }

    notifyLoadInstruments() {
        this.loadInstruments();
    }

    $onChanges(changes) {
        const currentPaymentErrorStates = changes.paymentErrorStates && changes.paymentErrorStates.currentValue || [];
        if (currentPaymentErrorStates.length) {
            // Filter unknown/notset instrument type error states.
            const unknownInstrumentErrorStates = filter(currentPaymentErrorStates, (value) => {
                const state = value.state;
                return state.ErrorCode && state.PaymentInstrumentType === this.settings.digitalPayPaymentTypes.unknownType;
            });

            if (some(unknownInstrumentErrorStates, errorState => errorState.state.ErrorCode === this.settings.digitalPayErrorCodes.paymentInstrumentNotFound)) {
                const errorMessage = this.digitalPayErrorMessageGenerator.getGenericPaymentError(this.settings.digitalPayErrorCodes.paymentInstrumentNotFound);
                this.digitalPayPaymentErrorDisplayService.pushGlobalError(errorMessage);
                this.preSelectPaymentInstruments();
            }
        }
    }
}

DigitalPayInstrumentsController.$inject = ['$q', 'DigitalPayPaymentDistributionService', 'ShopperService', 'Settings', 'DigitalPayDisablePaymentStateService', 'DateHelperService', 'DigitalPayPaymentErrorDisplayService', 'DigitalPayErrorMessageGenerator', 'DigitalPayPaymentInstrumentsService'];

const digitalPayInstrumentsComponentDefinition = {
    controller: DigitalPayInstrumentsController,
    template: require('./digital-pay-instruments.component.html').trim(),
    bindings: {
        accountPassword: '<',
        hasPrePaymentActionFailures: '<',
        hasPrePaymentValidationFailure: '<',
        isForPaymentAgreement: '<',
        onAccountPasswordFieldLoaded: '&',
        onChildIsInManageMode: '&',
        onPasswordReset: '&',
        paymentErrorStates: '<',
        paymentStateDefinitions: '<',
        prePaymentActionDefinitions: '<',
        shouldRemoveNewlyAddedInstruments: '<',
        shouldShowPayingAmount: '<',
        shouldShowPaypalPlaceOrderButton: '=',
        onCreditCardSelectionChanged: '&',
    },
};


module.exports = {
    DigitalPayInstrumentsController,
    digitalPayInstrumentsComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/digital-pay-instruments.component.js